'use client';

import type { PostArticleProps } from '@routeroyal/theme-core';
import { MarkdownRenderer, useMarkdownHeadings } from '@routeroyal/ui-core';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

/**
 * Stone Investment Post Article
 * 
 * Full article display with:
 * - Editorial typography
 * - Greyscale featured image
 * - Refined share buttons
 * - Elegant related posts
 */
export function StoneInvestmentPostArticle({
  post,
  relatedPosts = [],
  showShareButtons = true,
  showAuthorBio = true,
  showRelatedPosts = true,
  showTableOfContents = false,
  onShare,
  locale = 'en',
  basePath = '/news',
  className = '',
  messages,
}: PostArticleProps) {
  const [activeHeadingId, setActiveHeadingId] = useState<string>();

  // Format date
  const formattedDate = post.publishedAt 
    ? new Date(post.publishedAt).toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  // Extract headings from content for TOC using the core hook
  const allHeadings = useMarkdownHeadings(post.content, post.contentFormat);
  const headings = showTableOfContents ? allHeadings : [];

  // Track active heading for TOC
  useEffect(() => {
    if (!showTableOfContents || headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeadingId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' }
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings, showTableOfContents]);

  // Handle share
  const handleShare = (platform: 'twitter' | 'facebook' | 'linkedin' | 'copy') => {
    if (onShare) {
      onShare(platform);
      return;
    }

    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = post.title;

    switch (platform) {
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        break;
    }
  };

  return (
    <article className={`max-w-4xl mx-auto ${className}`}>
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-12"
      >
        {/* Category */}
        {post.category && (
          <span 
            className="text-[10px] tracking-[0.25em] uppercase text-[var(--color-text-muted)] mb-6 block"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {post.category.name}
          </span>
        )}

        {/* Title */}
        <h1 
          className="text-3xl md:text-4xl lg:text-5xl text-[var(--color-text)] mb-6 leading-tight"
          style={{ 
            fontFamily: "'Cormorant Garamond', 'Georgia', serif",
            fontWeight: 400,
          }}
        >
          {post.title}
        </h1>

        {/* Excerpt */}
        {post.excerpt && (
          <p 
            className="text-lg text-[var(--color-text-muted)] mb-8 leading-relaxed"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {post.excerpt}
          </p>
        )}

        {/* Meta */}
        <div 
          className="flex flex-wrap items-center gap-4 text-xs text-[var(--color-text-muted)] py-6 border-y border-[var(--color-border)]"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {/* Author */}
          <div className="flex items-center gap-3">
            {post.author.avatar || post.author.avatarUrl ? (
              <div className="relative w-10 h-10 overflow-hidden grayscale">
                <Image
                  src={post.author.avatar || post.author.avatarUrl || ''}
                  alt={post.author.name}
                  fill
                  className="object-cover"
                />
              </div>
            ) : (
              <div className="w-10 h-10 bg-[var(--color-border)] flex items-center justify-center">
                <span className="text-[var(--color-text-muted)] text-sm">
                  {post.author.name.charAt(0)}
                </span>
              </div>
            )}
            <div>
              <div className="font-medium text-[var(--color-text)]">{post.author.name}</div>
              {(post.author.role || post.author.authorTitle) && (
                <div className="text-[10px] uppercase tracking-wider">
                  {post.author.role || post.author.authorTitle}
                </div>
              )}
            </div>
          </div>

          <span className="text-[var(--color-border)]">|</span>

          {/* Date */}
          {formattedDate && <span>{formattedDate}</span>}

          {/* Reading time */}
          {(post.readingTime || post.readingTimeMinutes) && (
            <>
              <span className="text-[var(--color-border)]">|</span>
              <span>{post.readingTime || post.readingTimeMinutes} min read</span>
            </>
          )}

          {/* Share buttons */}
          {showShareButtons && (
            <div className="flex items-center gap-2 ml-auto">
              {['twitter', 'linkedin', 'copy'].map((platform) => (
                <button
                  key={platform}
                  onClick={() => handleShare(platform as 'twitter' | 'linkedin' | 'copy')}
                  className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
                  aria-label={`Share on ${platform}`}
                >
                  {platform === 'twitter' && (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  )}
                  {platform === 'linkedin' && (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  )}
                  {platform === 'copy' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </motion.header>

      {/* Featured Image */}
      {post.featuredImage && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative w-full aspect-[16/9] mb-12 overflow-hidden bg-[#1a1a1a]"
        >
          <Image
            src={post.featuredImage.url}
            alt={post.featuredImage.alt || post.title}
            fill
            className="object-cover grayscale"
            priority
          />
          {post.featuredImage.caption && (
            <div 
              className="absolute bottom-0 left-0 right-0 bg-[var(--color-text)]/80 text-[var(--color-background)] text-xs p-4"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {post.featuredImage.caption}
            </div>
          )}
        </motion.div>
      )}

      {/* Content Area */}
      <div className={`relative ${showTableOfContents && headings.length > 0 ? 'lg:grid lg:grid-cols-[1fr_200px] lg:gap-12' : ''}`}>
        {/* Table of Contents (Desktop) */}
        {showTableOfContents && headings.length > 0 && (
          <aside className="hidden lg:block lg:order-2">
            <div className="sticky top-24">
              <h4 
                className="text-[10px] tracking-[0.25em] uppercase text-[var(--color-text-muted)] mb-4"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Contents
              </h4>
              <ul className="space-y-2 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {headings.map((heading) => (
                  <li
                    key={heading.id}
                    style={{ paddingLeft: `${(heading.level - 2) * 0.75}rem` }}
                  >
                    <button
                      onClick={() => {
                        const element = document.getElementById(heading.id);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className={`
                        text-left w-full py-1 border-l pl-3 -ml-px
                        transition-all duration-200
                        ${activeHeadingId === heading.id
                          ? 'border-[var(--color-text)] text-[var(--color-text)]'
                          : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                        }
                      `}
                    >
                      {heading.text}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}

        {/* Article Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="lg:order-1"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          <MarkdownRenderer
            content={post.content}
            contentFormat={post.contentFormat}
            theme="stone"
          />
        </motion.div>
      </div>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-[var(--color-border)]"
        >
          {post.tags.map((tag) => (
            <span
              key={tag.id}
              className="px-3 py-1 text-[10px] tracking-[0.15em] uppercase text-[var(--color-text-muted)] border border-[var(--color-border)]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {tag.name}
            </span>
          ))}
        </motion.div>
      )}

      {/* Author Bio */}
      {showAuthorBio && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 pt-8 border-t border-[var(--color-border)]"
        >
          <div className="flex gap-6">
            {post.author.avatar || post.author.avatarUrl ? (
              <div className="relative w-16 h-16 flex-shrink-0 overflow-hidden grayscale">
                <Image
                  src={post.author.avatar || post.author.avatarUrl || ''}
                  alt={post.author.name}
                  fill
                  className="object-cover object-top"
                />
              </div>
            ) : (
              <div className="w-16 h-16 flex-shrink-0 bg-[var(--color-border)] flex items-center justify-center">
                <span className="text-[var(--color-text-muted)] text-xl">
                  {post.author.name.charAt(0)}
                </span>
              </div>
            )}
            <div>
              <h3 
                className="text-xl mb-1"
                style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}
              >
                {post.author.name}
              </h3>
              {(post.author.role || post.author.authorTitle) && (
                <p 
                  className="text-[10px] tracking-[0.2em] uppercase text-[var(--color-text-muted)] mb-3"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {post.author.role || post.author.authorTitle}
                </p>
              )}
              {(post.author.bio || post.author.authorBio) && (
                <p 
                  className="text-sm text-[var(--color-text-muted)] leading-relaxed"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {post.author.bio || post.author.authorBio}
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Related Posts */}
      {showRelatedPosts && relatedPosts.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 pt-12 border-t border-[var(--color-border)]"
        >
          <h2 
            className="text-2xl mb-8"
            style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}
          >
            {messages?.relatedPosts || 'Related Articles'}
          </h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {relatedPosts.map((relatedPost) => (
              <Link
                key={relatedPost.id}
                href={`${basePath}/${relatedPost.slug}`}
                className="group block"
              >
                {relatedPost.featuredImage && (
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#1a1a1a] mb-4">
                    <Image
                      src={relatedPost.featuredImage.url}
                      alt={relatedPost.title}
                      fill
                      className="object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                    />
                  </div>
                )}
                <h3 
                  className="text-lg text-[var(--color-text)] group-hover:text-[var(--color-text-muted)] transition-colors duration-300"
                  style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}
                >
                  {relatedPost.title}
                </h3>
              </Link>
            ))}
          </div>
        </motion.section>
      )}
    </article>
  );
}
